import { useEffect, useState, useMemo } from "react";
import { useGoldRates } from "@/lib/nonblocking-rates";
import { GRAMS_PER_TOLA, type Country } from "@/lib/gold-data";

/**
 * Diaspora Geo-Targeting PSEO layer — purely additive, injected into the
 * existing Pakistan/India country + city components. Provides:
 *  - Live 8-currency conversion card (below hero, above tables)
 *  - Deep overseas-diaspora SEO content block (mid-page)
 *  - Bidirectional link mesh footer for expat routing
 *  - Triple JSON-LD schema injection (FAQ, multi-currency Product, Speakable)
 *
 * All FX is client-fetched from api.exchangerate.host with hard-coded
 * ballpark fallbacks so SSR paints instantly with zero CLS. Every
 * container carries an explicit min-height per the user directive.
 */

const FALLBACK_FX_PER_USD: Record<string, number> = {
  USD: 1, GBP: 0.79, SAR: 3.75, AED: 3.67, CAD: 1.36, AUD: 1.52,
  SGD: 1.34, MYR: 4.72, BDT: 119.5,
};

const PK_CURRENCIES = ["USD", "GBP", "SAR", "AED", "CAD", "AUD", "BDT", "MYR"] as const;
const IN_CURRENCIES = ["USD", "GBP", "SAR", "AED", "CAD", "AUD", "SGD", "MYR"] as const;

const CURRENCY_META: Record<string, { flag: string; symbol: string; label: string; region: string; digits: number }> = {
  USD: { flag: "🇺🇸", symbol: "$",    label: "US Dollar",         region: "United States",       digits: 2 },
  GBP: { flag: "🇬🇧", symbol: "£",    label: "British Pound",     region: "United Kingdom",      digits: 2 },
  SAR: { flag: "🇸🇦", symbol: "SAR",  label: "Saudi Riyal",       region: "Saudi Arabia",        digits: 2 },
  AED: { flag: "🇦🇪", symbol: "AED",  label: "UAE Dirham",        region: "United Arab Emirates",digits: 2 },
  CAD: { flag: "🇨🇦", symbol: "C$",   label: "Canadian Dollar",   region: "Canada",              digits: 2 },
  AUD: { flag: "🇦🇺", symbol: "A$",   label: "Australian Dollar", region: "Australia",           digits: 2 },
  BDT: { flag: "🇧🇩", symbol: "৳",    label: "Bangladeshi Taka",  region: "Bangladesh",          digits: 0 },
  SGD: { flag: "🇸🇬", symbol: "S$",   label: "Singapore Dollar",  region: "Singapore",           digits: 2 },
  MYR: { flag: "🇲🇾", symbol: "RM",   label: "Malaysian Ringgit", region: "Malaysia",            digits: 2 },
};

function fmt(n: number, code: string) {
  const m = CURRENCY_META[code];
  const digits = m?.digits ?? 2;
  return `${m?.symbol ?? ""}${new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n)}`;
}

// Live FX fetch (cached upstream by exchangerate.host). Falls back silently.
function useLiveFx(codes: readonly string[]) {
  const [fx, setFx] = useState<Record<string, number>>(FALLBACK_FX_PER_USD);
  useEffect(() => {
    let cancelled = false;
    const symbols = codes.join(",");
    fetch(`https://api.exchangerate.host/latest?base=USD&symbols=${symbols}`, {
      cache: "force-cache",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: any) => {
        if (cancelled || !d?.rates) return;
        setFx((prev) => ({ ...prev, ...d.rates }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [codes]);
  return fx;
}

export function DiasporaRateCard({ country, cityName }: { country: Country; cityName?: string }) {
  const { data } = useGoldRates();
  const codes = country === "pakistan" ? PK_CURRENCIES : IN_CURRENCIES;
  const fx = useLiveFx(codes);
  const usdPerGram = data.spotUsdPerOz / 31.1035;
  const usdPerTola = usdPerGram * GRAMS_PER_TOLA;
  const usdPer10g = usdPerGram * 10;
  const label = cityName ?? (country === "pakistan" ? "Pakistan" : "India");

  const rows = useMemo(
    () =>
      codes.map((c) => {
        const mult = fx[c] ?? FALLBACK_FX_PER_USD[c] ?? 1;
        return {
          code: c,
          tola: usdPerTola * mult,
          ten: usdPer10g * mult,
          gram: usdPerGram * mult,
        };
      }),
    [codes, fx, usdPerTola, usdPer10g, usdPerGram],
  );

  return (
    <section
      aria-label="Diaspora multi-currency live gold conversion"
      className="mx-auto max-w-5xl px-4 pt-4"
    >
      <div
        className="diaspora-rate-summary rounded-2xl border border-primary/30 bg-gradient-to-br from-card via-card to-primary/5 p-4 md:p-6 shadow-sm"
        style={{ minHeight: 420 }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">
              💱 Diaspora Live Multi-Currency Card · 8 currencies
            </div>
            <h2 className="mt-1 text-lg md:text-xl font-bold">
              Today 24K {label} Gold Rate — Live Diaspora Currencies
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              Live spot × today&apos;s FX. For overseas Pakistanis in KSA, UAE, US, UK, Canada, Australia, Bangladesh, Malaysia; NRIs add Singapore.
            </p>
          </div>
          <div className="text-[10px] text-muted-foreground">Ref FX · api.exchangerate.host</div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {rows.map((r) => {
            const m = CURRENCY_META[r.code];
            return (
              <div
                key={r.code}
                className="rounded-xl border border-border bg-background/60 p-3 flex flex-col gap-1"
                style={{ minHeight: 118 }}
              >
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span aria-hidden>{m.flag}</span>
                  <span>{r.code}</span>
                  <span className="text-[11px] font-normal text-muted-foreground truncate">{m.label}</span>
                </div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">Per Tola (11.66 g)</div>
                <div className="text-base font-bold tabular-nums">{fmt(r.tola, r.code)}</div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Per 10 Grams</div>
                <div className="text-sm font-semibold tabular-nums">{fmt(r.ten, r.code)}</div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Live spot × today&apos;s FX (cached 1 hour). Actual retail rate in your city may differ due to duty, VAT &amp; local jeweller premium.
        </p>
      </div>

      {/* Schema 2 — multi-currency Product with per-region Offers */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: `24K Gold Rate in ${label} — Multi-Currency`,
            description: `Live 24K gold price in ${label} converted into ${codes.length} diaspora currencies for overseas ${country === "pakistan" ? "Pakistanis" : "Indians (NRIs)"}.`,
            category: "Precious Metals / Gold",
            image: ["https://drhint.com/icon-512.png"],
            brand: { "@type": "Brand", name: "DrHint" },
            offers: rows.slice(0, 7).map((r) => ({
              "@type": "Offer",
              priceCurrency: r.code,
              price: r.tola.toFixed(2),
              description: `1 tola (11.66 g) 24K gold in ${r.code}`,
              availability: "https://schema.org/InStock",
              eligibleRegion: { "@type": "Country", name: CURRENCY_META[r.code].region },
              url: `https://drhint.com/${country}${cityName ? `/${cityName.toLowerCase()}` : ""}`,
              hasMerchantReturnPolicy: {
                "@type": "MerchantReturnPolicy",
                applicableCountry: CURRENCY_META[r.code].region,
                returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
              },
              shippingDetails: {
                "@type": "OfferShippingDetails",
                shippingRate: { "@type": "MonetaryAmount", value: "0", currency: r.code },
                shippingDestination: { "@type": "DefinedRegion", addressCountry: CURRENCY_META[r.code].region },
                deliveryTime: {
                  "@type": "ShippingDeliveryTime",
                  handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
                  transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
                },
              },
            })),
          }),
        }}
      />
      {/* Schema 3 — Speakable spec for voice assistants */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: `Today gold rate in ${label} — diaspora currencies`,
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: [".diaspora-rate-summary", ".diaspora-deep h2", ".diaspora-deep h3"],
            },
            url: `https://drhint.com/${country}${cityName ? `/${cityName.toLowerCase()}` : ""}`,
          }),
        }}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Deep diaspora content block — mid page                            */
/* ------------------------------------------------------------------ */

const CORRIDORS = [
  { name: "Wise (mid-market)",  spread: 0.005, note: "Closest to interbank; best for buying gold-backed savings." },
  { name: "Remitly",            spread: 0.012, note: "Small margin; instant transfers to bank / cash pickup." },
  { name: "Western Union",      spread: 0.022, note: "Wider spread; useful for cash pickup in small towns." },
];

const TZ_TABLE = [
  { city: "Riyadh / Jeddah (KSA)", tz: "AST (UTC+3)",  open: "10:00", close: "22:00" },
  { city: "Dubai / Abu Dhabi (UAE)", tz: "GST (UTC+4)", open: "10:00", close: "22:00" },
  { city: "London (UK)",           tz: "GMT (UTC+0)",  open: "05:00", close: "17:00" },
  { city: "New York (USA)",        tz: "EST (UTC-5)",  open: "00:00", close: "12:00" },
  { city: "Toronto (Canada)",      tz: "EST (UTC-5)",  open: "00:00", close: "12:00" },
  { city: "Sydney (Australia)",    tz: "AEDT (UTC+11)", open: "16:00", close: "04:00 (+1)" },
];

export function DiasporaDeepContent({ country, cityName }: { country: Country; cityName?: string }) {
  const cName = country === "pakistan" ? "Pakistan" : "India";
  const place = cityName ?? cName;
  const audience = country === "pakistan" ? "Overseas Pakistanis" : "NRIs";
  const currency = country === "pakistan" ? "PKR" : "INR";
  const { data } = useGoldRates();
  const usdPerTola = (data.spotUsdPerOz / 31.1035) * GRAMS_PER_TOLA;

  const faqs = [
    { q: `What is today's gold rate in ${place} in USD?`, a: `Today's live 24K gold rate in ${place} is approximately $${usdPerTola.toFixed(2)} per tola (11.66 g) and $${((data.spotUsdPerOz / 31.1035) * 10).toFixed(2)} per 10 grams, converted from the international spot price.` },
    { q: `Can I bring gold legally from Dubai to ${cName}?`, a: `Yes. Male passengers can bring up to 20 g and female passengers up to 40 g of gold jewellery duty-free to ${cName}. Bars above the limit are dutiable — check the current customs slab before travel.` },
    { q: `Is gold cheaper in ${place} compared to Riyadh?`, a: `${place} gold prices are usually 2–5% higher than Riyadh's Souk Al Zahab because of local sales tax, jeweller premium and import duty. The multi-currency card above lets you compare instantly.` },
    { q: `What is today's gold rate in ${place} in SAR?`, a: `See the SAR row in the Diaspora Multi-Currency card above — refreshed live against today's SAR/USD (pegged at 3.75).` },
    { q: `Can I remit money from UAE to buy gold in ${place}?`, a: `Yes. Wise, Remitly and Western Union all support AED→${currency} corridors. Compare corridor spreads in the exchange table below before locking a wire.` },
    { q: `Which karat is standard in ${place} vs USA?`, a: `${place} jewellery is dominated by 22K (916). In the USA, 14K and 10K are far more common. Overseas ${audience.toLowerCase()} buying in ${place} typically choose 22K or 24K for higher resale value.` },
    { q: `When is the ${place} Sarafa Bazaar open for diaspora buyers?`, a: `${place} jeweller bazaars typically open 10:00–20:00 local. Refer to the time-zone table below for KSA, UAE, UK, USA and Australia equivalents.` },
    { q: `Do overseas Pakistanis / NRIs pay VAT on ${place} gold?`, a: `No VAT is charged on investment-grade 24K bars in most GCC states; jewellery purchased locally in the UAE carries 5% VAT. Local ${cName} purchases include applicable GST/sales tax.` },
    { q: `Is the rate on this page the same 24 hours a day?`, a: `The rate refreshes every 10 minutes from the international spot feed + live USD/${currency} FX, so overseas users see the same live number regardless of timezone.` },
    { q: `How accurate is the multi-currency card for wire transfers?`, a: `Very accurate as a reference — the FX is mid-market from api.exchangerate.host. Actual remittance rates carry a corridor spread of 0.5–2.5% depending on provider (see corridor table below).` },
  ];

  return (
    <section
      aria-label={`Diaspora overseas content — ${place}`}
      className="mx-auto max-w-4xl px-4 py-8 diaspora-deep"
      style={{ minHeight: 520 }}
    >
      <div className="rounded-2xl border border-border bg-card p-5 md:p-6">
        <h2 className="text-xl md:text-2xl font-bold">
          {place} Gold Rate for {audience} in USA, UK, Saudi Arabia &amp; UAE
        </h2>

        <h3 className="mt-5 text-lg font-semibold">🇸🇦 Saudi Arabia — Remittance &amp; Souk Comparison</h3>
        <p className="mt-1 text-sm text-foreground/85">
          Buying gold in Riyadh&apos;s Souk Al Zahab or Jeddah&apos;s Balad district is typically 2–4% cheaper than {place} retail due to zero VAT on investment-grade 24K bars. {audience} sending SAR home should watch the {currency}/SAR corridor — a 1% FX swing changes the effective tola price by roughly {(usdPerTola * 3.75 * 0.01).toFixed(0)} SAR.
        </p>

        <h3 className="mt-5 text-lg font-semibold">🇦🇪 UAE / Dubai — Customs &amp; Deira Gold Souk</h3>
        <p className="mt-1 text-sm text-foreground/85">
          Male passengers can carry up to 20 g and female passengers up to 40 g of gold jewellery duty-free into {cName}; anything above (up to 32 g in some slabs) attracts duty. Dubai Deira Gold Souk pricing is transparent and posted per 10 g in AED — compare against the AED row in the card above before flying.
        </p>

        <h3 className="mt-5 text-lg font-semibold">🇬🇧 UK — VAT &amp; London / Birmingham Premium</h3>
        <p className="mt-1 text-sm text-foreground/85">
          Investment-grade 24K bars are VAT-exempt in the UK, but jewellery bought retail in London&apos;s Hatton Garden or Birmingham&apos;s Jewellery Quarter carries 20% VAT plus a ~3–6% retailer premium. The GBP row shows the pure metal cost — anything above that in a UK shop is duty + margin.
        </p>

        <h3 className="mt-5 text-lg font-semibold">🇺🇸 USA — Karat Availability Difference</h3>
        <p className="mt-1 text-sm text-foreground/85">
          Retail jewellery in the USA is dominated by 14K and 10K — 22K/24K are hard to find outside diaspora districts (Jackson Heights NY, Devon Ave Chicago, Edison NJ). {audience} shopping in the US should compare the USD row above to any diaspora-store quote to spot premium.
        </p>

        <h3 className="mt-5 text-lg font-semibold">🕐 Time-Zone Reference — When is the {place} Sarafa Open?</h3>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="text-left">
                <th className="py-2 pr-3">Diaspora City</th>
                <th className="py-2 pr-3">Timezone</th>
                <th className="py-2 pr-3 text-right">Bazaar Opens</th>
                <th className="py-2 pr-0 text-right">Bazaar Closes</th>
              </tr>
            </thead>
            <tbody>
              {TZ_TABLE.map((t) => (
                <tr key={t.city} className="border-t border-border">
                  <td className="py-2 pr-3 font-medium">{t.city}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.tz}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{t.open}</td>
                  <td className="py-2 pr-0 text-right tabular-nums">{t.close}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="mt-5 text-lg font-semibold">💸 Exchange Corridors — Live Remittance Spread</h3>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="text-left">
                <th className="py-2 pr-3">Corridor</th>
                <th className="py-2 pr-3 text-right">Est. Spread</th>
                <th className="py-2 pr-0">Best For</th>
              </tr>
            </thead>
            <tbody>
              {CORRIDORS.map((c) => (
                <tr key={c.name} className="border-t border-border">
                  <td className="py-2 pr-3 font-medium">{c.name}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{(c.spread * 100).toFixed(2)}%</td>
                  <td className="py-2 pr-0 text-xs text-muted-foreground">{c.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Schema 1 — Diaspora FAQPage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Bidirectional link mesh footer                                     */
/* ------------------------------------------------------------------ */

const PK_MESH = ["lahore", "karachi", "islamabad", "rawalpindi", "faisalabad", "multan", "peshawar", "quetta", "sialkot", "gujranwala"];
const IN_MESH = ["delhi", "mumbai", "bangalore", "chennai", "kolkata", "hyderabad", "pune", "ahmedabad", "jaipur", "surat"];

function anchorVariant(city: string, i: number): string {
  const c = city.charAt(0).toUpperCase() + city.slice(1);
  const variants = [
    `${c} gold rate today`,
    `24K gold price in ${c} (multi-currency)`,
    `Sarafa Bazaar ${c} live rate`,
    `Today ${c} gold rate for overseas expats`,
    `${c} 22K gold price — live diaspora feed`,
  ];
  return variants[i % variants.length];
}

export function DiasporaLinkMesh({ country, cityName }: { country: Country; cityName?: string }) {
  const own = country === "pakistan" ? PK_MESH : IN_MESH;
  const sib = own.filter((s) => s !== cityName?.toLowerCase()).slice(0, 10);
  const cross = country === "pakistan" ? IN_MESH.slice(0, 5) : PK_MESH.slice(0, 5);
  const crossCountry: Country = country === "pakistan" ? "india" : "pakistan";

  return (
    <section
      aria-label="Related live rates for overseas expats"
      className="mx-auto max-w-5xl px-4 py-6"
      style={{ minHeight: 200 }}
    >
      <div className="rounded-2xl border border-border bg-card p-4 md:p-5">
        <h2 className="text-base md:text-lg font-bold">Related Live Rates for Overseas Expats</h2>
        <div className="mt-3 grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {sib.map((s, i) => (
            <a
              key={s}
              href={`/${country}/${s}`}
              className="rounded-lg border border-border bg-background/60 px-3 py-2 text-xs md:text-sm font-medium hover:border-primary hover:bg-primary/5 transition-colors truncate"
            >
              {anchorVariant(s, i)} →
            </a>
          ))}
        </div>
        <h3 className="mt-5 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Cross-border: {crossCountry === "pakistan" ? "top Pakistan cities" : "top India cities"}
        </h3>
        <div className="mt-2 grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
          {cross.map((s, i) => (
            <a
              key={s}
              href={`/${crossCountry}/${s}`}
              className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs font-semibold hover:border-primary transition-colors truncate"
            >
              {anchorVariant(s, i + 2)}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
